<div class="comments-wrapper">
  <!-- Header Section -->
  <div class="comments-header">
    <h4 class="comments-title">
      Comments
      <span *ngIf="sectionId" class="section-label" [matTooltip]="sectionId">
        ({{ sectionId }})
      </span>
    </h4>
    
    <!-- Status Filter -->
    <div class="status-filter">
      <button 
        class="filter-btn" 
        [class.active]="selectedStatus === 'all'" 
        (click)="setFilter('all')">
        All <span class="count-badge">{{ getStatusCount('all') }}</span>
      </button>
      <button 
        class="filter-btn" 
        [class.active]="selectedStatus === 'open'" 
        (click)="setFilter('open')">
        Open <span class="count-badge">{{ getStatusCount('open') }}</span>
      </button>
      <button 
        class="filter-btn" 
        [class.active]="selectedStatus === 'resolved'" 
        (click)="setFilter('resolved')">
        Resolved <span class="count-badge">{{ getStatusCount('resolved') }}</span>
      </button>
      <button 
        class="filter-btn" 
        [class.active]="selectedStatus === 'closed'" 
        (click)="setFilter('closed')">
        Closed <span class="count-badge">{{ getStatusCount('closed') }}</span>
      </button>
    </div>
  </div>

  <!-- Comments Container - Scrollable Area -->
  <div class="comments-scroll-area">
    <!-- Comments List -->
    <div class="comments-list" *ngIf="filteredComments.length; else noComments">
      <!-- Comment Item -->
      <div 
        class="comment-item" 
        *ngFor="let comment of filteredComments" 
        [class.highlighted]="comment.highlight"
        [class.has-replies]="comment.replies.length > 0">
        
        <!-- Comment Header -->
        <div class="comment-header">
          <div class="comment-avatar">
            <span class="avatar-initials">{{ getInitials(comment.author) }}</span>
          </div>
          
          <div class="comment-meta">
            <div class="comment-author">
              <span class="author-name">{{ comment.author }}</span>
              <span class="author-role">({{ comment.role }})</span>
            </div>
            <div class="comment-info">
              <span class="comment-time">
                {{ formatTimestamp(comment.timestamp) || "-" }}
              </span>
            </div>
          </div>
          
          <div class="comment-status" [ngClass]="comment.status">
            {{ comment.status }}
          </div>
        </div>

        <!-- Comment Body -->
        <div class="comment-body" *ngIf="editingComment !== comment">
          <p class="comment-text">{{ comment.text }}</p>
        </div>

        <!-- Edit Comment -->
        <div class="comment-edit" *ngIf="editingComment === comment">
          <textarea 
            class="edit-textarea"
            [(ngModel)]="editText" 
            placeholder="Edit comment..."
            rows="2">
          </textarea>
          <div class="edit-actions">
            <button 
              class="btn-save"
              (click)="saveEdit()">
              <i class="fas fa-check"></i> Save
            </button>
            <button 
              class="btn-cancel"
              (click)="cancelEdit()">
              <i class="fas fa-times"></i> Cancel
            </button>
          </div>
        </div>

        <!-- Replies Section -->
        <div class="replies-section" *ngIf="comment.replies.length">
          <div class="replies-list">
            <div 
              class="reply-item" 
              *ngFor="let reply of comment.replies"
              [class.highlighted]="reply.highlight">
              
              <!-- Reply Header -->
              <div class="reply-header">
                <div class="reply-avatar">
                  <span class="avatar-initials">{{ getInitials(reply.author) }}</span>
                </div>
                
                <div class="reply-meta">
                  <div class="reply-author">
                    <span class="author-name">{{ reply.author }}</span>
                    <span class="author-role">({{ reply.role }})</span>
                  </div>
                  <div class="reply-info">
                    <span class="reply-time">
                      {{ formatTimestamp(reply.timestamp) || "-" }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Reply Body -->
              <div class="reply-body">
                <p class="reply-text">{{ reply.text }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Reply Box -->
        <div class="reply-box" *ngIf="activeReplyId === comment.commentId">
          <textarea 
            class="reply-textarea"
            [(ngModel)]="replyText" 
            placeholder="Type reply..."
            rows="2"
            (keydown.enter)="onEnter($event, 'reply', comment)">
          </textarea>
          <div class="reply-actions">
            <button 
              class="btn-primary"
              (click)="sendReply(comment)">
              <i class="fas fa-paper-plane"></i> Send
            </button>
            <button 
              class="btn-secondary"
              (click)="activeReplyId = null">
              Cancel
            </button>
          </div>
        </div>

        <!-- Comment Actions -->
        <div class="comment-actions">
          <!-- Reviewer Actions -->
          <button 
            *ngIf="role?.toLowerCase() === 'reviewer' && (comment.status === 'open' || comment.status === 'resolved') && (comment.role === 'author' || comment.role === 'reviewer')" 
            class="action-btn" 
            (click)="closeComment(comment.commentId)" 
            title="Close Comment">
            <i class="fas fa-lock"></i>
          </button>

          <button 
            *ngIf="role?.toLowerCase() === 'reviewer' && comment.status === 'closed' && (comment.role === 'author' || comment.role === 'reviewer')" 
            class="action-btn" 
            (click)="reopenComment(comment.commentId)" 
            title="Reopen Comment">
            <i class="fas fa-lock-open"></i>
          </button>

          <!-- Author Actions -->
          <button 
            *ngIf="canResolve && comment.status === 'open'" 
            class="action-btn" 
            (click)="resolveComment(comment.commentId)" 
            title="Mark as Resolved">
            <i class="fas fa-check-circle"></i>
          </button>

          <!-- Approver Actions -->
          <button 
            *ngIf="role === 'approver' && (comment.status === 'open' || comment.status === 'resolved') && (comment.role === 'author' || comment.role === 'reviewer' || comment.role === 'approver')" 
            class="action-btn" 
            (click)="closeComment(comment.commentId)" 
            title="Close Comment">
            <i class="fas fa-lock"></i>
          </button>

          <button 
            *ngIf="role === 'approver' && comment.status === 'closed' && (comment.role === 'author' || comment.role === 'reviewer' || comment.role === 'approver')" 
            class="action-btn" 
            (click)="reopenComment(comment.commentId)" 
            title="Reopen Comment">
            <i class="fas fa-lock-open"></i>
          </button>

          <!-- Common Actions -->
          <button 
            class="action-btn" 
            (click)="startReply(comment.commentId)" 
            title="Reply">
            <i class="fas fa-share fa-flip-horizontal"></i>
          </button>
          
          <button 
            *ngIf="comment.status !== 'closed'" 
            class="action-btn" 
            (click)="editComment(comment)" 
            title="Edit">
            <i class="fas fa-edit"></i>
          </button>
          
          <button 
            *ngIf="comment.status !== 'closed'" 
            class="action-btn" 
            (click)="deleteComment(comment)" 
            title="Delete">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- No Comments State -->
    <ng-template #noComments>
      <div class="no-comments-state">
        <div class="no-comments-icon">
          <i class="far fa-comment"></i>
        </div>
        <p class="no-comments-message">{{ getNoCommentsMessage() }}</p>
      </div>
    </ng-template>
  </div>

  <!-- Compact New Comment Form -->
  <div class="new-comment-form">
    <textarea 
      class="new-comment-input"
      [(ngModel)]="commentText" 
      placeholder="Add a comment..."
      rows="1"
      (keydown.enter)="onEnter($event, 'comment')">
    </textarea>
    
    <div class="form-controls">
      <button 
        class="btn-submit"
        (click)="addComment()"
        [disabled]="!commentText.trim()">
        <i class="fas fa-paper-plane"></i> Send
      </button>
    </div>
  </div>
</div>